import { formatDate } from '@seen/core/dates'
import { Participant } from '@seen/core/models/patients/participant'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType } from '@seen/core/utils'
import {
  Button,
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  cn,
} from '@seen/ui'
import { IconLink } from '@tabler/icons-react'
import { type JSX, type ReactNode, useRef, useState } from 'react'
import { ContextMenuMedplumLinkItem } from '#src/components/ContextMenuMedplumLinkItem.tsx'
import { ContextMenuOpenInNewTab } from '#src/components/ContextMenuOpenInNewTab.tsx'
import { ContextMenuDeleteItem } from '#src/components/core/context-menu/ContextMenuDeleteItem.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { HoverCreatedOn } from '#src/components/HoverCreatedOn.tsx'
import { IconDue } from '#src/components/icons/IconDue.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { LabelRow } from '#src/components/task/LabelRow.tsx'
import { TaskRowErrorBoundary } from '#src/components/task/list/TaskRowErrorBoundary.tsx'
import { OrderBadge } from '#src/components/task/OrderBadge.tsx'
import { OwnerIconCombobox } from '#src/components/task/OwnerIconCombobox.tsx'
import { TaskPriorityCombobox } from '#src/components/task/TaskPriorityCombobox.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { TaskStateCombobox } from '#src/components/task/TaskStateCombobox.tsx'
import { TaskStateText } from '#src/components/task/TaskStateText.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import { TaskRowCheckbox } from '#src/components/TaskRowCheckbox.tsx'
import { ParticipantReferenceContext } from '#src/contexts/current-participant/ParticipantReferenceContext.tsx'
import {
  createTaskFilter,
  useMarkRelatedInboxItemsAsDone,
} from '#src/hooks/inbox/index.ts'
import { useEventListener } from '#src/hooks/useEventListener.ts'
import { useLinkOrigin } from '#src/hooks/useLinkOrigin.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { extractTitleFromHTML } from '#src/utils/html.ts'
import { toast } from '#src/utils/toast.ts'

export interface TaskRowProps {
  task: StandardTask
  hidePriority?: boolean
  hideParticipant?: boolean
  hideOrder?: boolean
  hideAuthoredOn?: boolean
  hideLabels?: boolean
  hideDueDate?: boolean
  hideOwner?: boolean
  hideCheckbox?: boolean
  titleClassName?: string
  className?: string
  checked?: boolean
  onCheckedChange?: (value: boolean, isShiftClick?: boolean) => void
  prefix?: ReactNode
}

export function TaskRow(props: TaskRowProps): JSX.Element {
  return (
    <TaskRowErrorBoundary
      task={props.task}
      hideCheckbox={props.hideCheckbox}
      prefix={props.prefix}
      className={props.className}
    >
      <TaskRowContent {...props} />
    </TaskRowErrorBoundary>
  )
}

function TaskRowContent({
  task,
  hidePriority,
  hideParticipant,
  hideOrder,
  hideAuthoredOn,
  hideLabels,
  hideDueDate,
  hideOwner,
  hideCheckbox,
  titleClassName,
  className,
  checked,
  onCheckedChange,
  prefix,
}: TaskRowProps): JSX.Element {
  const linkOrigin = useLinkOrigin()
  const patchTask = usePatchMutation<StandardTask>().mutate

  const { data: taskParticipant } = useRead(Participant, task?.participant)

  const containerRef = useRef<HTMLElement>(null)
  const [parentContainerWidth, setParentContainerWidth] = useState(
    containerRef.current?.parentElement?.clientWidth
  )
  useEventListener('resize', () => {
    setParentContainerWidth(containerRef.current?.parentElement?.clientWidth)
  })

  // TaskRow appears in various layouts, so we dynamically set maxLabelsToShow based on the parent container's width
  // to prevent content overflow and ensure responsive display.
  let maxLabelsToShow
  if (parentContainerWidth && parentContainerWidth < 825) {
    maxLabelsToShow = 0
  } else if (parentContainerWidth && parentContainerWidth < 1000) {
    maxLabelsToShow = 1
  }

  const markRelatedInboxItemsAsDone = useMarkRelatedInboxItemsAsDone({
    silent: true,
  })

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild ref={containerRef}>
        <Link
          to={
            taskParticipant
              ? `${taskParticipant.relativeUrl}/tasks/${task.id}`
              : `/tasks/${task.id}`
          }
          className={cn(
            'group flex min-h-10 items-center px-pagex-default hover:bg-accent data-[state=open]:bg-accent lg:px-pagex-lg',
            checked && 'bg-accent',
            !hideCheckbox && 'pr-8 pl-0 lg:pr-8 lg:pl-0',
            className
          )}
        >
          {!hideCheckbox && (
            <TaskRowCheckbox value={checked} onChange={onCheckedChange} />
          )}
          {prefix}
          <div className="flex flex-1 items-center gap-2">
            {!hidePriority && (
              <div onClick={(e) => e.preventDefault()} className="self-stretch">
                <ResourceFieldForm
                  instance={task}
                  valueFn={(t) => t.priority ?? null}
                  patchFn={(t, value) => {
                    t.priority = value ?? undefined
                  }}
                  className="h-full"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <TaskPriorityCombobox onSelect={onChange} selected={value}>
                      <Button
                        variant="unstyled"
                        className="h-full p-0 hover:brightness-hover-icon data-[state=open]:brightness-hover-icon"
                        {...rest}
                      >
                        <TaskPriorityIcon
                          priority={value ?? undefined}
                          isTaskCompletedOrCancelled={
                            task.isCompletedOrCancelled
                          }
                          tooltip={
                            StandardTask.priorityText(value ?? undefined) ??
                            'No priority'
                          }
                        />
                      </Button>
                    </TaskPriorityCombobox>
                  )}
                />
              </div>
            )}
            <div onClick={(e) => e.preventDefault()} className="self-stretch">
              <ResourceFieldForm
                instance={task}
                valueFn={(t) => t.state}
                patchFn={(t, value) => {
                  t.state = value
                }}
                mutationOptions={{
                  onSuccess: (instance) => {
                    markRelatedInboxItemsAsDone(
                      createTaskFilter([
                        {
                          taskReferenceString: task.reference.reference,
                          state: instance.state,
                        },
                      ])
                    )
                    if (instance.state === 'done') {
                      toast.success('Task marked done.')
                    }
                  },
                }}
                className="h-full"
                render={({ field: { value, onChange, ...rest } }) => (
                  <TaskStateCombobox onSelect={onChange} selected={value}>
                    <Button
                      variant="unstyled"
                      className="h-full p-0 hover:brightness-hover-icon data-[state=open]:brightness-hover-icon"
                      {...rest}
                    >
                      <IconTaskState state={value} />
                    </Button>
                  </TaskStateCombobox>
                )}
              />
            </div>
            <div className="flex flex-1 items-center gap-2">
              <ParticipantReferenceContext.Provider value={task.participant}>
                <RichText
                  value={task.title}
                  className={cn(
                    'line-clamp-1 max-w-none font-medium text-sm [&_p]:max-w-none',
                    titleClassName
                  )}
                />
              </ParticipantReferenceContext.Provider>
              {task.participant && !hideParticipant && (
                <ParticipantTaskRowBadge participant={task.participant} />
              )}
            </div>
            {!hideLabels && (
              <div className="hidden sm:block">
                <LabelRow
                  task={task}
                  onClick={(e) => e.preventDefault()}
                  maxLabelsToShow={maxLabelsToShow}
                />
              </div>
            )}
            {isOfType(task.focus, 'ServiceRequest') && !hideOrder && (
              <div className="hidden sm:block">
                <OrderBadge order={task.focus} />
              </div>
            )}
            {!hideDueDate && task.dueOn && (
              <RowBadge
                icon={
                  <IconDue
                    dateString={task.dueOn}
                    isCompleted={task.isCompletedOrCancelled}
                    className="size-3.5"
                  />
                }
              >
                {formatDate(task.dueOn)}
              </RowBadge>
            )}
            {!hideAuthoredOn && (
              <HoverCreatedOn
                className="hidden sm:block"
                createdOn={task.authoredOn}
              />
            )}
            {!hideOwner && (
              <div onClick={(e) => e.preventDefault()} className="self-stretch">
                <ResourceFieldForm
                  instance={task}
                  valueFn={(task) => task.ownerPractitioner?.reference ?? null}
                  patchFn={(task, value) => {
                    task.ownerPractitioner = value
                      ? { reference: value }
                      : undefined
                  }}
                  className="h-full"
                  render={({ field }) => <OwnerIconCombobox {...field} />}
                />
              </div>
            )}
          </div>
        </Link>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuSub>
          <ContextMenuSubTrigger className="gap-2">
            <IconTaskState
              state="in-progress"
              className="[&_svg]:stroke-muted-foreground"
            />
            Status
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {StandardTask.stateSchema.options.map((state) => (
              <StateItem key={state} task={task} state={state} />
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuItem
          className="gap-2"
          onSelect={() => {
            void navigator.clipboard.writeText(
              `${linkOrigin()}/tasks/${task.id}`
            )
            toast.info('Task URL copied to clipboard')
          }}
        >
          <IconLink className="size-4 stroke-muted-foreground" />
          Copy URL
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuDeleteItem
          deleteConfirmationProps={{
            title: `Delete "${extractTitleFromHTML(task.title)}"?`,
            description:
              'Deleted tasks can still be recovered by contacting our team.',
            onConfirm: () =>
              patchTask({
                instance: task,
                mutateFn: (t) => t.delete(),
              }),
          }}
        />
        <ContextMenuSeparator />
        <ContextMenuOpenInNewTab href={`/tasks/${task.id}`} />
        <ContextMenuMedplumLinkItem to={task} />
      </ContextMenuContent>
    </ContextMenu>
  )
}

function StateItem({
  state,
  task,
}: {
  state: StandardTask['state']
  task: StandardTask
}): JSX.Element {
  const patchMutation = usePatchMutation<StandardTask>()
  return (
    <ContextMenuCheckboxItem
      onSelect={() =>
        patchMutation.mutate({
          instance: task,
          mutateFn: (task) => {
            task.state = state
          },
        })
      }
      checked={task.state === state}
      className="gap-2"
    >
      <IconTaskState state={state} />
      <TaskStateText state={state} />
    </ContextMenuCheckboxItem>
  )
}
